import { Box } from '@mui/material';
import { UPDropdown } from 'components/dropdown';
import UPInput from 'components/input';
import { noop, toNumber } from 'lodash';
import { ChangeEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  COMPANY_PAYROLL__FACTOR_S,
  COMPANY_PAYROLL__HOURS_PER_DAY,
  COMPANY_PAYROLL__NOT_CONFIGURED,
  COMPANY_PAYROLL__WORKING_SYSTEM,
  COMPANY_PAYROLL__WORKING_SYSTEM_DAY,
  COMPANY_PAYROLL__WORKING_SYSTEM_DAYS,
} from 'translations/constants';
import {
  WORKING_SYSTEM_DAYS,
  calculateHoursPerDayMinutes,
  formatHoursPerDay,
  isTotalHoursInput,
  validateTotalHours,
} from './helpers';
import { IntervalHourBox, IntervalText } from './styles';
import { WorkScheduleFieldsProps } from './types';

const EMPTY_WORKING_SYSTEM = '–';

// Working system, Total hours per week and the calculated Hours per day, shared by the fixed and flexible sections
const WorkScheduleFields = (props: WorkScheduleFieldsProps) => {
  const { workingSystem, totalHours, allowEmptyTotalHours, stacked, onChangeWorkingSystem, onChangeTotalHours } = props;
  const [t] = useTranslation();

  // the empty choice is an option of its own instead of the dropdown's clear button, whose icon blocks the arrow
  const workingSystemItems = useMemo(
    () => [
      { value: '', label: EMPTY_WORKING_SYSTEM },
      ...WORKING_SYSTEM_DAYS.map(days => ({
        value: String(days),
        label: `${days} ${t(days === 1 ? COMPANY_PAYROLL__WORKING_SYSTEM_DAY : COMPANY_PAYROLL__WORKING_SYSTEM_DAYS)}`,
      })),
    ],
    [t],
  );

  const { isValid, errorKey } = validateTotalHours(totalHours, allowEmptyTotalHours);
  const hoursPerDayMinutes = calculateHoursPerDayMinutes(workingSystem, isValid ? totalHours : null);

  const workingSystemField = (
    <UPDropdown
      label={stacked ? String(t(COMPANY_PAYROLL__WORKING_SYSTEM)) : ''}
      placeholder={EMPTY_WORKING_SYSTEM}
      items={workingSystemItems}
      value={workingSystem ? String(workingSystem) : ''}
      onChange={event => onChangeWorkingSystem(event.target.value ? toNumber(event.target.value) : null)}
    />
  );

  const totalHoursField = (
    <UPInput
      value={totalHours ?? ''}
      maxLength={7}
      error={!!errorKey}
      helperText={errorKey ? String(t(errorKey)) : ''}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        if (!isTotalHoursInput(value)) return;
        onChangeTotalHours(value, validateTotalHours(value, allowEmptyTotalHours).isValid);
      }}
      placeholder="0.00"
      label={stacked ? String(t(COMPANY_PAYROLL__FACTOR_S)) : ''}
    />
  );

  const hoursPerDayField = (
    <UPInput
      value={
        hoursPerDayMinutes === null ? String(t(COMPANY_PAYROLL__NOT_CONFIGURED)) : formatHoursPerDay(hoursPerDayMinutes)
      }
      onChange={noop}
      placeholder=""
      label={stacked ? String(t(COMPANY_PAYROLL__HOURS_PER_DAY)) : ''}
      disabled
    />
  );

  if (stacked) {
    return (
      <Box display="flex" flexWrap="wrap" gap="24px">
        <Box width="200px">{workingSystemField}</Box>
        <Box width="200px">{totalHoursField}</Box>
        <Box width="200px">{hoursPerDayField}</Box>
      </Box>
    );
  }

  return (
    <>
      <IntervalHourBox>
        <IntervalText sx={{ width: '70%' }}>{t(COMPANY_PAYROLL__WORKING_SYSTEM)}</IntervalText>
        <Box sx={{ width: '35%', minWidth: '120px' }}>{workingSystemField}</Box>
      </IntervalHourBox>
      <IntervalHourBox>
        <IntervalText sx={{ width: '70%' }}>{t(COMPANY_PAYROLL__FACTOR_S)}</IntervalText>
        <Box sx={{ width: '35%' }}>{totalHoursField}</Box>
      </IntervalHourBox>
      <IntervalHourBox>
        <IntervalText sx={{ width: '70%' }}>{t(COMPANY_PAYROLL__HOURS_PER_DAY)}</IntervalText>
        <Box sx={{ width: '35%', minWidth: '130px' }}>{hoursPerDayField}</Box>
      </IntervalHourBox>
    </>
  );
};

export default WorkScheduleFields;
